import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import Home from '@/views/Home.vue'; // 使用现有Home组件
import MainContent from '@/components/MainContent.vue';
import Cookies from 'js-cookie';
import { ElMessage } from 'element-plus';
import { useUserStore } from '@/stores/userStore';

const medicalDepartments = ['respiratory', 'orthopedics', 'cardiovascular', 'neurology'] as const; // 科室列表

const routes: RouteRecordRaw[] = [
    {
        path: '/',
        name: 'Home',
        component: Home,
        children: [
            {
                path: '',
                name: 'HomeDefault',
                component: MainContent,
            },
            {
                path: '/images',
                name: 'Images',
                component: () => import('../views/Images.vue'),
            },
            {
                path: '/videos',
                name: 'Videos',
                component: () => import('@/views/VideoHub.vue'),
            },
            {
                path: '/courses',
                name: 'Courses',
                component: () => import('../views/Course/Courses.vue'),
            },
            {
                path: '/coursesDetail',
                name: 'CoursesDetail',
                component: () => import('@/views/Course/CourseDetail.vue'),
            },
            // 课件中心
            {
                path: '/courseware',
                name: 'Courseware',
                component: () => import('@/views/Courseware/index.vue'),
                children: medicalDepartments.map(dept => ({
                    path: dept,
                    component: () => import(`@/views/Courseware/${dept}.vue`),
                })),
            },
            // 图片详情页
            {
                path: '/image/:id',
                name: 'ImageDetail',
                component: () => import('@/views/ImageDetail.vue'),
                meta: {
                    title: '图片详情',
                },
            },
            {
                path: '/instructions',
                name: 'Instructions',
                component: () => import('@/views/Instructions.vue'),
            },
            {
                path: '/instructionsDetail',
                name: 'InstructionsDetail',
                component: () => import('@/views/InstructionsDetail.vue'),
            },
            // 用户详情页
            {
                path: '/user/:id',
                name: 'UserDetail',
                component: () => import('@/views/UserDetail.vue'),
                meta: {
                    title: '用户详情',
                },
                beforeEnter: () => {
                    if (!Cookies.get('token')) {
                        ElMessage.error('请您先登录');
                        return '/';
                    }
                },
            },
            {
                path: '/user/profileEdit',
                name: 'UserProfileEdit',
                component: () => import('@/views/UserProfileEdit.vue'),
                meta: {
                    title: '用户资料编辑',
                },
                beforeEnter: () => {
                    if (!Cookies.get('token')) {
                        ElMessage.error('请您先登录');
                        return '/';
                    }
                },
            },
            {
                path: '/upload',
                name: 'Upload',
                component: () => import('@/views/Upload.vue'),
                meta: {
                    title: '文件上传',
                },
                beforeEnter: () => {
                    if (!Cookies.get('token')) {
                        ElMessage.error('请您先登录');
                        return '/';
                    }
                    const userStore = useUserStore();
                    if (userStore.getUserInfo?.role !== 1) {
                        ElMessage.error('您没有权限上传文件');
                        return '/';
                    }
                },
            },
            // 内容管理页面
            {
                path: '/content',
                name: 'ContentManagement',
                component: () => import('@/views/ContentManagement.vue'),
                meta: {
                    title: '内容管理',
                },
                beforeEnter: () => {
                    if (!Cookies.get('token')) {
                        ElMessage.error('请您先登录');
                        return '/';
                    }
                },
            },
            {
                path: '/favorite',
                name: 'Favorite',
                component: () => import('@/views/Favorite.vue'),
                beforeEnter: () => {
                    if (!Cookies.get('token')) {
                        ElMessage.error('请您先登录');
                        return '/';
                    }
                },
            },
            // 病例库（带科室分类）
            {
                path: '/cases',
                name: 'Cases',
                component: () => import('@/views/Cases/index.vue'),
                children: medicalDepartments.map(dept => ({
                    path: dept,
                    name: `Cases${dept.charAt(0).toUpperCase() + dept.slice(1)}`,
                    component: () => import(`@/views/Cases/${dept}.vue`),
                })),
            },
            // 医学图库
            {
                path: '/atlas',
                name: 'NanshanImages',
                component: () => import('@/views/Atlas/NanshanImaging.vue'),
            },
        ],
    },

    // 登录页面
    {
        path: '/login',
        name: 'Login',
        component: () => import('@/views/Login.vue'),
        meta: {
            title: '用户登录',
        },
    },

    // 注册页面
    {
        path: '/register',
        name: 'Register',
        component: () => import('@/views/Register.vue'),
        meta: {
            title: '用户注册',
        },
    },

    // 内容编辑页面
    {
        path: '/content/edit/:id',
        name: 'ContentEdit',
        component: () => import('@/views/ContentManagement.vue'),
        meta: {
            title: '编辑内容',
        },
    },
    // 内容创建页面
    {
        path: '/content/create',
        name: 'ContentCreate',
        component: () => import('@/views/ContentManagement.vue'),
        meta: {
            title: '创建内容',
        },
    },

    // 微课视频（外链处理）
    {
        path: '/micro-lectures',
        name: 'MicroLectures',
        component: () => import('@/components/ExternalRedirect.vue'),
        props: () => ({
            url: 'https://external-video-platform.com/medical',
        }),
    },

    // 资源外链
    {
        path: '/resources/:target?',
        name: 'Resources',
        component: { template: '<div></div>' },
        beforeEnter: to => {
            //设置外部连接白名单
            const safeDomains = [
                { url: 'https://www.baidu.com', name: '百度' },
                {
                    url: 'https://www.dxy.cn/',
                    name: '丁香园',
                },
                {
                    url: 'https://www.medlive.cn/',
                    name: '医脉通',
                },
                {
                    url: 'https://library.cmu.edu.cn/',
                    name: '中国医科大学图书馆',
                },
                {
                    url: 'https://www.91huayi.com/',
                    name: '华医网',
                },
                {
                    url: 'https://www.gyfyyy.cn/',
                    name: '广州医科大学附属第一医院官方网站',
                },
            ];

            // 验证外部链接安全性
            const targetUrl = safeDomains.find(d => d.name === to.query.category)?.url;

            if (targetUrl) {
                window.open(targetUrl, '_blank');
            } else {
                console.warn('试图访问未经验证的域名:', targetUrl);
            }
            return false; // 阻止继续导航
        },
    },
    {
        path: '/external',
        name: 'ExternalRedirect',
        component: () => import('@/components/ExternalRedirect.vue'),
        beforeEnter: to => {
            if (!to.query.url) {
                return '/404';
            }
        },
    },

    {
        path: '/404',
        name: 'NotFound',
        component: () => import('@/views/NotFound.vue'),
    },
    {
        path: '/:pathMatch(.*)*', // 捕获所有未匹配路径
        redirect: '/404', // 重定向到404页面
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        // 始终滚动到顶部
        return { top: 0 };
    },
});

router.afterEach(async (to, from) => {
    const userStore = useUserStore();
    const isTokenValid = await userStore.checkAuth();
    if (!isTokenValid) {
        userStore.logout(); // 如果存在token，则执行登出操作
    }
});

// 在路由配置文件底部添加全局错误处理
router.onError(error => {
    if (/Loading chunk .+ failed/.test(error.message)) {
        // 处理医疗资源加载失败的情况
        router.push({
            name: 'NotFound',
            query: {
                type: 'chunk_error',
                from: router.currentRoute.value.fullPath,
            },
        });
    }
});

export default router;
